import { useCallback, useMemo } from "react";
import {
  catalogBySlug,
  proposeTodaySession,
  todayGapSets,
  type Exercise,
  type PainMap,
  type PlanStarter,
  type TimedSessionResult,
  type TodayGap,
  type TodayGapSets,
} from "@heraion/shared";

import type { MuscleCoverage } from "../Progress/muscleCoverage";
import { sunkSlugsAt, type ProposalScope } from "../Progress/muscleCoverageProposal";
import { todayGapsFromCoverage } from "../Progress/todayGapsFromCoverage";
import type { StartBuiltSessionInput } from "./useWorkoutStarter";

// What the empty «today» card needs from the host's coverage (D-c): whether
// «Build one anyway» has anything to build, the build itself, and whether no
// muscle is under its weekly minimum. Shared by the mobile and web mounts; the
// starter seam (useWorkoutStarter.buildTodayStarter) is theirs. A draft with
// candidates builds «anyway» on its own first (useTodayDraft). #540: the
// rebuilt draft carries both sets (todayGapSets), so its toggle stays.

/** What the empty card reads from the host. */
export type TodayEmptyHost = {
  onBuildAnyway: (() => Promise<PlanStarter | null>) | undefined;
  weeklyMinimumMet: boolean;
};

/** The host before its coverage is loaded: no button, no fact. */
export const IDLE_TODAY_EMPTY_HOST: TodayEmptyHost = {
  onBuildAnyway: undefined,
  weeklyMinimumMet: false,
};

export function useTodayAnyway({
  coverage,
  catalog,
  painMap,
  bodyweightOnly = false,
  place,
  results = null,
  buildTodayStarter,
}: {
  coverage: MuscleCoverage | null;
  catalog: ReadonlyArray<Exercise> | null;
  painMap: PainMap | undefined;
  /** The draft was bodyweight only: the rebuilt one opens so too. */
  bodyweightOnly?: boolean;
  /** The athlete's place and the results its absences read (sunkSlugsAt). */
  place?: ProposalScope;
  results?: ReadonlyArray<TimedSessionResult> | null;
  buildTodayStarter: (input: StartBuiltSessionInput) => Promise<PlanStarter | undefined>;
}): TodayEmptyHost {
  // Antenore 07/10: «anyway» ignores the rest. It builds today's own gaps, most
  // rested first; with none under MEV, a MAV gap from every banded row. Rest
  // ignored, only the pain map can empty it, so an empty history is enough to
  // know the button would build something.
  const index = useMemo(() => (catalog ? catalogBySlug(catalog) : null), [catalog]);
  // Viable on the set the draft's toggle had on.
  const { sets, viable } = useMemo((): { sets: TodayGapSets | null; viable: boolean } => {
    if (!catalog || !index) return { sets: null, viable: false };
    const sunk = sunkSlugsAt(place, results ?? []);
    const setsOf = (g: TodayGap[]) => todayGapSets(g, catalog, (ex) => !sunk.has(ex.slug));
    const builds = (s: TodayGapSets) => {
      const g = bodyweightOnly ? s.bodyweightGaps : s.gaps;
      return (
        g.length > 0 &&
        proposeTodaySession({
          gaps: g,
          catalog: index,
          painMap,
          history: new Map(),
          nowMs: 0,
          ignoreRest: true,
        }).view.rows.length > 0
      );
    };
    const own = setsOf(todayGapsFromCoverage(coverage));
    if (builds(own)) return { sets: own, viable: true };
    const anyway = setsOf(todayGapsFromCoverage(coverage, { mode: "anyway" }));
    return { sets: anyway, viable: builds(anyway) };
  }, [coverage, catalog, index, painMap, place, results, bodyweightOnly]);
  const build = useCallback(
    async () =>
      catalog && sets
        ? ((await buildTodayStarter({
            ...sets,
            catalog,
            painMap,
            ignoreRest: true,
            ...(bodyweightOnly ? { bodyweightOnly: true } : {}),
          })) ?? null)
        : null,
    [buildTodayStarter, sets, catalog, painMap, bodyweightOnly],
  );
  return {
    onBuildAnyway: viable ? build : undefined,
    weeklyMinimumMet: coverage !== null && !coverage.incomplete && coverage.belowMevCount === 0,
  };
}
